import { Typography, Stack, Box, IconButton, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { Icon as Iconify } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../../store/appStore';


export default function RetailerWiseProductWiseSchemeHeader({ headerName, exportToPDF, hidePdfButton=false, exportToExcel, handleOpenFilter, handleOpenCopyScheme, additionalHeaderContent }) {

    const {
        hoverFilterValues
    } = useAppStore();

    const navigate = useNavigate();

    const handleCreate = () => {
        navigate(`${window.location.pathname}/add`);
    };

    //  console.log('hoverFilterValues : ', hoverFilterValues);
    const downloadPDF = () => {
        exportToPDF();
    };

    const downloadExcel = async () => {
        exportToExcel();
    };

    const openFilter = () => {
        handleOpenFilter()
    }

    const buttons = [
        {
            title: 'Filter',
            icon: 'mdi:filter',
            onClick: openFilter,
            sx: {
                boxShadow: 'inset 0 0 0 #b3b3b3, 0 5px 0 0 #b3b3b3, 0 10px 5px #999999 !important',
                padding: "6px",
                height: "32px",
                width: "35px",
                border: '0.1px solid #e0e0e0',
                margin: '0 -9px 0 16px' // Add margin to reduce padding between buttons
            }
        },        
        {
            title: 'Export To Excel',
            icon: 'vscode-icons:file-type-excel',
            onClick: downloadExcel,
            sx: {
                bgcolor: '#a6dbbc',
                color: '#fff',
                padding: "6px",
                boxShadow: 'inset 0 0 0 #b3b3b3, 0 5px 0 0 #b3b3b3, 0 10px 5px #999999 !important',
                height: "32px",
                width: "35px",
                margin: '0 -9px 0 16px' // Add margin to reduce padding between buttons
            }
        },
        ...(hidePdfButton
            ? []
            : [{
                title: 'Export To PDF',
                icon: 'icomoon-free:file-pdf',
                onClick: downloadPDF,
                sx: {
                    bgcolor: '#ED5565',
                    color: '#fff',
                    boxShadow: 'inset 0 0 0 #b3b3b3, 0 5px 0 0 #b3b3b3, 0 10px 5px #999999 !important',
                    padding: "8px",
                    height: "32px",
                    width: "35px",
                    margin: '0 -12px 0 16px'
                }
            }])
    ];

    const formatKey = (key) => {
        if (!key) return '';
        let cleanKey = key.replace(/id$/i, '');
        if (/^[A-Z0-9]+$/.test(cleanKey)) {
            cleanKey = cleanKey.toLowerCase();
        } else {
            cleanKey = cleanKey.replace(/([A-Z])/g, ' $1').trim();
        }
        return cleanKey.charAt(0).toUpperCase() + cleanKey.slice(1);
    };

    const formatValue = (value, key) => {

        if (Array.isArray(value)) {
            return value.map(val => formatValue(val, key)).filter(Boolean).join(', ');
        }

        if (typeof value === 'object' && value !== null) {

            // Special handling for ReportFilter object
            if (value?.ReportFilter) {
                return value.ReportFilter;
            }

            // Special handling for SchemeGroup object
            if (value?.SchemeGroup) {
                return value.SchemeGroup;
            }

            // Special handling for Distributor object
            if (value?.Distributor) {
                return value.Distributor;
            }

            // Optional: SchemeName support (future safe)
            if (value?.SchemeName) {
                return value.SchemeName;
            }

            return Object.entries(value)
                .filter(([k, val]) => val && !/id$/i.test(k))
                .map(([, val]) => formatValue(val, key))
                .join(', ');
        }

        if (key === "ReportGeneratedOn") {
            const strVal = value?.toString();
            if (strVal === "1") return "On Pre Process Data";
            if (strVal === "0") return "On Live Data";
        }

        return value ? value.toString() : '';
    };

    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ padding: '0px 16px 10px 8px' }}
        >
            {/* Header Title */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ fontSize: '1rem', lineHeight: '0.9rem' }}>
                    {headerName}
                </Typography>
            </Box>

            {/* Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {additionalHeaderContent}
                {buttons.map((button, index) => {
                    let tooltipTitle = button.title;
                    if (button.title === 'Filter') {
                        const validFilterEntries = hoverFilterValues
                            ? Object.entries(hoverFilterValues).filter(([, value]) => {
                                if (value === undefined || value === null) return false;
                                if (value === '') return false;
                                if (Array.isArray(value) && value.length === 0) return false;
                                if (value === 0) return false; //  hides ShowDetails when 0
                                return true;
                            })
                            : [];


                        if (validFilterEntries.length > 0) {
                            tooltipTitle = (
                                <Box sx={{
                                    bgcolor: 'white', color: '#000', p: 1, borderRadius: 1,
                                    maxWidth: 200,
                                    maxHeight: 200,
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                }}>
                                    <Stack direction="column" alignItems='flex-start' justifyContent='flex-start' spacing={1}>
                                        {validFilterEntries.map(([key, value]) => (
                                            <Typography variant='body2' key={key}>
                                                <span style={{ fontWeight: 'bold' }}>
                                                    {formatKey(key)}:
                                                </span>
                                                <span style={{ fontSize: '13px' }}> {formatValue(value, key)}</span>
                                            </Typography>
                                        ))}
                                    </Stack>
                                </Box>
                            );
                        } else {
                            tooltipTitle = 'Filter';
                        }
                    }


                    return (
                        <Tooltip key={index} title={tooltipTitle}> { /* NOSONAR: Index as key is acceptable due to non-reordering static list */}
                            <IconButton
                                onClick={button.onClick}
                                sx={{ ...button.sx, borderRadius: '8px' }}
                            >
                                <Iconify icon={button.icon} />
                            </IconButton>
                        </Tooltip>
                    );
                })}
            </Box>
        </Stack>
    );
}

RetailerWiseProductWiseSchemeHeader.propTypes = {
    headerName: PropTypes.any.isRequired,
    exportToPDF: PropTypes.func.isRequired,
    hidePdfButton: PropTypes.bool.isRequired,
    exportToExcel: PropTypes.func.isRequired,
    handleOpenFilter: PropTypes.func.isRequired,
    handleOpenCopyScheme: PropTypes.func,
    additionalHeaderContent: PropTypes.any,
};
